import type { InstallRoute } from './install';

/**
 * The install invite's rules (decision 110, #418), kept pure so the tests can walk them all:
 *
 * - phones and tablets only: a computer has the account menu's «Installa l'app»;
 * - only where installing is possible and not done (#303's route: `prompt` or `ios-guide`);
 * - from the first session, right after sign-in: the shell, which holds it, renders only once
 *   someone is signed in, so the sign-in page never shows it (the Director, #418);
 * - «Non ora» hides it for 30 days on the device; «È già installata» (iPhone and iPad, where Safari
 *   cannot tell) stops it for good;
 * - the new-version band wins: the two never show together.
 */
export type DeviceClass = 'phone' | 'tablet' | 'desktop';
export type InviteRoute = Extract<InstallRoute, 'prompt' | 'ios-guide'>;

export interface InviteFacts {
  readonly route: InstallRoute;
  readonly device: DeviceClass;
  readonly dismissedAt: number | null;
  readonly never: boolean;
  readonly updateShown: boolean;
  readonly now: number;
}

export const SNOOZE_MS = 30 * 86_400_000;

export function inviteFor(facts: InviteFacts): InviteRoute | null {
  if (facts.device === 'desktop' || facts.updateShown || facts.never) {
    return null;
  }
  if (facts.route !== 'prompt' && facts.route !== 'ios-guide') {
    return null;
  }
  if (facts.dismissedAt !== null && facts.now - facts.dismissedAt < SNOOZE_MS) {
    return null;
  }
  return facts.route;
}

/** A touch screen whose short side is 600 px or more is a tablet (iPad, Android tablets). */
export function deviceClassFor({
  coarse,
  shortSide,
}: {
  coarse: boolean;
  shortSide: number;
}): DeviceClass {
  if (!coarse) {
    return 'desktop';
  }
  return shortSide >= 600 ? 'tablet' : 'phone';
}

export function currentDeviceClass(): DeviceClass {
  const coarse = window.matchMedia('(pointer: coarse)').matches;
  return deviceClassFor({ coarse, shortSide: Math.min(window.screen.width, window.screen.height) });
}

// --- what the device remembers (localStorage; a store that cannot be read means «show») ---------

const DISMISSED_AT = 'dgb-console:install-invite:dismissed-at';
const NEVER = 'dgb-console:install-invite:never';

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // A store that cannot be written only means the invite may come back sooner.
  }
}

export function snoozeInvite(now = Date.now()): void {
  write(DISMISSED_AT, String(now));
}

export function stopInvite(): void {
  write(NEVER, '1');
}

export function storedFacts(): Pick<InviteFacts, 'dismissedAt' | 'never'> {
  const dismissed = Number(read(DISMISSED_AT));
  return {
    dismissedAt: read(DISMISSED_AT) !== null && Number.isFinite(dismissed) ? dismissed : null,
    never: read(NEVER) === '1',
  };
}
